import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import is from '@sindresorhus/is';
import { AnimatePresence, motion } from 'framer-motion';
import type { FunctionComponent, ReactNode } from 'react';
import { Fragment, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import type { JobArchitectureItemStatistics } from '~/shared/data-contract/job-architecture';
import type { JobArchitectureProfileReviewer } from '~/shared/data-contract/job-profile';
import { IconLevel, IconPeopleAdd, IconRole, PersonCircleIcon } from '~/ui/core/icons';
import { Tooltip } from '~/ui/core/tooltip/Tooltip';

import { DraftBadge } from './DraftBadge';
import { NewVersionBadge } from './NewVersionBadge';
import { PersonProgressIcon } from './PersonProgressIcon';
import { PublishedBadge } from './PublishedBadge';
import { PublishProgressIcon } from './PublishProgressIcon';
import { ReviewedBadge } from './ReviewedBadge';
import { ReviewProgressIcon } from './ReviewProgressIcon';
import { ReviewSection } from './ReviewSection';

const PROGRESS_COLOR_THRESHOLD = 0.1;

const Container = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    fontVariantNumeric: 'tabular-nums',
    gap: theme.spacing.s,
}));

const getProgressColor = (progress: number, activeColor: string, inactiveColor: string): string => {
    return progress > PROGRESS_COLOR_THRESHOLD ? activeColor : inactiveColor;
};

const calculateProgress = (count: number, total: number): number => {
    return total > 0 ? count / total : 0;
};

const IconContainer = styled.span(() => ({
    alignItems: 'center',
    display: 'inline-flex',
    justifyContent: 'center',
}));

export const Separator: FunctionComponent = () => {
    return (
        // biome-ignore lint/a11y/noSvgWithoutTitle: suppressed due to migration
        <svg height="1rem" style={{ display: 'inline-block', stroke: 'currentColor' }} viewBox="0 0 2 10" width="1">
            <rect fill="currentColor" height="10" rx="0.5" width="1" x="0.666626" />
        </svg>
    );
};

export const ChildCount = styled.span<{ readonly 'data-testid'?: string }>(({ theme }) => ({
    ...theme.typography.body.XXS.medium,
    alignItems: 'center',
    display: 'inline-flex',
    flexShrink: 0,
    gap: theme.spacing.xxs,
    whiteSpace: 'nowrap',
}));

const ApprovedChildren = styled.span(({ theme }) => ({
    '[data-is-active="true"] &': {
        color: `${theme.colors.white[200]} !important`,
    },
    color: theme.colors.success[500],
    ...theme.typography.body.XXS.semibold,
}));

export const ChildStats = styled.div(({ theme }) => ({
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    gap: theme.spacing.s,
}));

export const ProgressIndicator: FunctionComponent<{
    readonly colorBase: string;
    readonly colorFill: string;
    readonly count: number;
    readonly 'data-testid'?: string;
    readonly icon: 'count' | 'publish' | 'review';
    readonly isActive: boolean;
    readonly progress: number;
    readonly showCount?: boolean;
    readonly tooltip: string;
}> = ({ colorBase, colorFill, count, 'data-testid': testId, icon, isActive, progress, showCount = true, tooltip }) => {
    const ProgressIcon =
        icon === 'review' ? ReviewProgressIcon : icon === 'publish' ? PublishProgressIcon : PersonProgressIcon;
    const countColor = getProgressColor(progress, colorFill, colorBase);

    if (progress === 0) {
        return null;
    }

    return (
        <Tooltip>
            <Tooltip.Trigger asChild={true}>
                <ReviewSection data-testid={testId} progress={progress}>
                    <IconContainer>
                        <ProgressIcon
                            colorBase={colorBase}
                            colorFill={colorFill}
                            isActive={isActive}
                            progress={progress}
                        />
                    </IconContainer>
                    {showCount && <ApprovedChildren style={{ color: countColor }}>{count}</ApprovedChildren>}
                </ReviewSection>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content side="top">{tooltip}</Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip>
    );
};

export const SimpleCount: FunctionComponent<{
    readonly children: ReactNode;
    readonly count: number;
    readonly 'data-testid'?: string;
    readonly tooltip: string;
}> = ({ children, count, 'data-testid': testId, tooltip }) => {
    return (
        <Tooltip>
            <Tooltip.Trigger asChild={true}>
                <ChildCount data-testid={testId}>
                    {children}
                    {count}
                </ChildCount>
            </Tooltip.Trigger>
            <Tooltip.Portal>
                <Tooltip.Content side="top">{tooltip}</Tooltip.Content>
            </Tooltip.Portal>
        </Tooltip>
    );
};

export const ReviewerBadge = styled.div<{ readonly isActive: boolean }>(({ isActive, theme }) => ({
    '& svg': {
        height: '100%',
        width: '100%',
    },
    alignContent: 'center',
    alignItems: 'center',
    backgroundColor: isActive ? theme.colors.orange[500] : theme.colors.gray[100],
    borderRadius: theme.borderRadius.full,
    color: isActive ? theme.colors.orange[100] : theme.colors.gray[700],
    cursor: 'default',
    display: 'flex',
    fontSize: theme.typography.body.XXXS.semibold.fontSize,
    height: theme.spacing['3xl'],
    justifyContent: 'center',
    justifyItems: 'center',
    padding: theme.spacing.xxs,
    width: theme.spacing['3xl'],
}));

const ChildrenStats: FunctionComponent<{
    readonly approvedChildrenCount: number;
    readonly assignedReviewersCount: number;
    readonly childCount: number;
    readonly isActive: boolean;
    readonly publishedChildren?: number;
}> = ({ approvedChildrenCount, assignedReviewersCount, childCount, isActive, publishedChildren }) => {
    const theme = useTheme();
    const { translate } = useContext(ApplicationContext);
    const childProgress = calculateProgress(approvedChildrenCount, childCount);

    return (
        <ChildStats>
            <SimpleCount
                count={childCount}
                data-testid="child-levels-count"
                tooltip={translate('pages.job-architecture-v2.statistics.level-count')}
            >
                <IconLevel size={12} />
            </SimpleCount>
            <SimpleCount
                count={assignedReviewersCount}
                data-testid="child-reviewers-count"
                tooltip={translate('pages.job-architecture-v2.statistics.levels-with-reviewers')}
            >
                <PersonCircleIcon />
            </SimpleCount>
            <ProgressIndicator
                colorBase={theme.colors.info[100]}
                colorFill={theme.colors.info[500]}
                count={approvedChildrenCount}
                data-testid="child-approved-count"
                icon="review"
                isActive={isActive}
                progress={childProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.reviewed-levels')}
            />
            {is.positiveNumber(publishedChildren) && (
                <ProgressIndicator
                    colorBase={isActive ? theme.colors.white[200] : theme.colors.success[100]}
                    colorFill={isActive ? theme.colors.white[200] : theme.colors.success[500]}
                    count={publishedChildren}
                    data-testid="child-published-count"
                    icon="publish"
                    isActive={isActive}
                    progress={childProgress}
                    tooltip={translate('pages.job-architecture-v2.statistics.published-levels')}
                />
            )}
        </ChildStats>
    );
};

const getInitial = (profile: { readonly reviewer?: JobArchitectureProfileReviewer }): string => {
    return (profile.reviewer?.firstName?.[0] ?? '') + (profile.reviewer?.lastName?.[0] ?? '');
};

export const Initials: FunctionComponent<{
    readonly profile: { readonly reviewer?: JobArchitectureProfileReviewer };
}> = ({ profile }) => {
    return profile.reviewer !== undefined ? (
        <span>{getInitial(profile)}</span>
    ) : (
        <IconPeopleAdd data-testid="reviewer-add-icon" />
    );
};

const ReviewStateBadge: FunctionComponent<{ readonly isActive: boolean; readonly isApproved: boolean }> = ({
    isActive,
    isApproved,
}) => {
    return (
        <AnimatePresence mode="wait">
            <motion.div
                key={isApproved ? 'reviewed' : 'draft'}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                initial={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
            >
                {isApproved ? <ReviewedBadge isActive={isActive} /> : <DraftBadge />}
            </motion.div>
        </AnimatePresence>
    );
};

const ProfileChildrenSection: FunctionComponent<{
    readonly isActive: boolean;
    readonly statistics: JobArchitectureItemStatistics;
}> = ({ isActive, statistics }) => {
    const hasChildren = is.positiveNumber(statistics.levelsCount);

    if (!hasChildren) {
        return null;
    }

    return (
        <Fragment>
            <Separator />
            <ChildrenStats
                approvedChildrenCount={statistics.approvedLevels ?? 0}
                assignedReviewersCount={statistics.reviewerLevels ?? 0}
                childCount={statistics.levelsCount ?? 0}
                isActive={isActive}
                publishedChildren={statistics.publishedLevels ?? 0}
            />
        </Fragment>
    );
};

export const ProfileStatus: FunctionComponent<{
    readonly isActive: boolean;
    readonly isApproved: boolean;
    readonly profile: { readonly reviewer?: JobArchitectureProfileReviewer };
    readonly published?: boolean;
    readonly statistics: JobArchitectureItemStatistics;
}> = ({ isActive, isApproved, profile, published, statistics }) => {
    const isPublished = is.boolean(published) && published;

    return (
        <Container data-testid="profile-status">
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <ReviewerBadge data-testid="reviewer-badge" isActive={isActive}>
                        <Initials profile={profile} />
                    </ReviewerBadge>
                </Tooltip.Trigger>
                {profile.reviewer !== undefined && (
                    <Tooltip.Content side="top">
                        {profile.reviewer?.firstName} {profile.reviewer?.lastName}
                    </Tooltip.Content>
                )}
            </Tooltip>
            <Separator />
            {(!isPublished || isApproved) && <ReviewStateBadge isActive={isActive} isApproved={isApproved} />}
            {isPublished && <PublishedBadge isActive={isActive} />}
            {!isApproved && isPublished && <NewVersionBadge isActive={isActive} />}
            <ProfileChildrenSection isActive={isActive} statistics={statistics} />
        </Container>
    );
};

export const Status: FunctionComponent<{
    readonly isActive: boolean;
    readonly statistics: JobArchitectureItemStatistics;
}> = ({ isActive, statistics }) => {
    const theme = useTheme();
    const { translate } = useContext(ApplicationContext);

    const approvedRoleProgress = calculateProgress(statistics.approvedRoles, statistics.rolesCount);
    const publishedRoleProgress = calculateProgress(statistics.publishedRoles, statistics.rolesCount);
    const assignedRoleProgress = calculateProgress(statistics.reviewerRoles, statistics.rolesCount);

    const hasRolesSection = statistics.rolesCount > 0;
    const hasLevelsSection = statistics.levelsCount > 0;

    return (
        <Container data-testid="status">
            {hasRolesSection && (
                <SimpleCount
                    count={statistics.rolesCount}
                    data-testid="roles-count"
                    tooltip={translate('pages.job-architecture-v2.statistics.role-count')}
                >
                    <IconRole size={12} />
                </SimpleCount>
            )}
            <ProgressIndicator
                colorBase={theme.colors.gray[700]}
                colorFill={theme.colors.gray[500]}
                count={statistics.reviewerRoles}
                data-testid="reviewer-roles-count"
                icon="count"
                isActive={isActive}
                progress={assignedRoleProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.roles-with-reviewers')}
            />
            <ProgressIndicator
                colorBase={theme.colors.info[100]}
                colorFill={theme.colors.info[500]}
                count={statistics.approvedRoles}
                data-testid="approved-roles-count"
                icon="review"
                isActive={isActive}
                progress={approvedRoleProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.reviewed-roles')}
            />
            <ProgressIndicator
                colorBase={isActive ? theme.colors.white[200] : theme.colors.success[100]}
                colorFill={isActive ? theme.colors.white[200] : theme.colors.success[500]}
                count={statistics.publishedRoles}
                data-testid="published-roles-count"
                icon="publish"
                isActive={isActive}
                progress={publishedRoleProgress}
                tooltip={translate('pages.job-architecture-v2.statistics.published-roles')}
            />
            {hasRolesSection && hasLevelsSection && <Separator />}
            {hasLevelsSection && (
                <SimpleCount
                    count={statistics.levelsCount}
                    data-testid="levels-count"
                    tooltip={translate('pages.job-architecture-v2.statistics.level-count')}
                >
                    <IconLevel size={12} />
                </SimpleCount>
            )}
        </Container>
    );
};
